import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import Step0Config from './Step0Config'
import Step1Meals from './Step1Meals'
import Step2Desserts from './Step2Desserts'
import Step3Review from './Step3Review'
import { useAuth } from '../../contexts/useAuth'
import type { Recipe, Dessert, ShoppingList } from '../../services/api'

interface WizardState {
    name: string
    selectedRecipes: Recipe[]
    desserts: Dessert[]
    nbPersons: number
    durationDays: number
    mealsPerDay: number
}

const STEPS = ['Configuration', 'Plats', 'Desserts', 'Révision']

export default function GenerateList() {
    const navigate = useNavigate()
    const [step, setStep] = useState(0)
    const [state, setState] = useState<WizardState>({
        name: `Liste du ${new Date().toLocaleDateString('fr-FR')}`,
        selectedRecipes: [],
        desserts: [],
        nbPersons: 2,
        durationDays: 7,
        mealsPerDay: 2,
    })
    const { isAuthenticated } = useAuth()
    const goNext = () => setStep((s: number) => Math.min(s + 1, 3))
    const goBack = () => {
        if (step === 3 && !isAuthenticated) {
            if (!confirm('Attention : votre liste n\'est pas sauvegardée. Si vous revenez en arrière, vous devrez la générer à nouveau. Continuer ?')) {
                return
            }
        }
        if (step === 0) navigate('/app')
        else setStep((s: number) => s - 1)
    }
    const update = (patch: Partial<WizardState>) => setState((prev: WizardState) => ({ ...prev, ...patch }))

    return (
        <div>
            {/* Top bar */}
            <div className="page-header">
                <button className="btn btn-ghost btn-sm btn-icon" onClick={goBack}>
                    <ChevronLeft size={20} />
                </button>
                <div>
                    <h1 style={{ fontSize: '1.2rem' }}>Générer une liste</h1>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>Étape {step + 1} : {STEPS[step]}</p>
                </div>
            </div>

            {/* Step indicator */}
            <div className="step-indicator">
                {STEPS.map((_label, i) => (
                    <div key={`step-group-${i}`} style={{ display: 'flex', alignItems: 'center', flex: i === STEPS.length - 1 ? 'none' : 1 }}>
                        <div className={`step-dot ${i === step ? 'active' : i < step ? 'done' : ''}`}>
                            {i < step ? '✓' : i + 1}
                        </div>
                        {i < STEPS.length - 1 && <div className={`step-line ${i < step ? 'done' : ''}`} />}
                    </div>
                ))}
            </div>

            <div className="fade-in" key={step}>
                {step === 0 && (
                    <Step0Config
                        state={state}
                        update={update}
                        onNext={goNext}
                    />
                )}
                {step === 1 && (
                    <Step1Meals
                        state={state}
                        update={update}
                        onNext={goNext}
                    />
                )}
                {step === 2 && (
                    <Step2Desserts
                        state={state}
                        update={update}
                        onNext={goNext}
                    />
                )}
                {step === 3 && (
                    <Step3Review
                        state={state}
                        onComplete={(list: ShoppingList) => navigate(`/shopping/${list.id}`, { state: { list } })}
                    />
                )}
            </div>
        </div>
    )
}
